import { gql, useMutation, useQuery } from "@apollo/client";

import { isSuperAdminRole } from "@/reporting/utils/isSuperAdminRole";
import { getErrorMessage, isOwnerRole } from "@zapier/reporting/src/utils";
import type {
  AutoReplayQuery,
  UpdateAutoReplayMutation,
} from "./useAutoReplay.generated";

type UseAutoReplayInput = {
  accountId: number | undefined;
};

export const getAutoReplayQuery = gql`
  query AutoReplay($accountId: ID!) {
    account(id: $accountId) {
      currentUserRole
      id
      isAutoReplayEnabled
      isUnlimited
      plan {
        id
        hasAutoReplay
      }
    }
  }
`;

const updateAutoReplay = gql`
  mutation UpdateAutoReplay($accountId: ID!, $isAutoReplayEnabled: Boolean!) {
    updateAccountAutoReplay(
      id: $accountId
      isAutoReplayEnabled: $isAutoReplayEnabled
    ) {
      id
      isAutoReplayEnabled
    }
  }
`;

function useAutoReplay({ accountId }: UseAutoReplayInput) {
  const queryResult = useQuery<AutoReplayQuery>(getAutoReplayQuery, {
    skip: !accountId,
    variables: { accountId },
  });

  const [update, updateResults] =
    useMutation<UpdateAutoReplayMutation>(updateAutoReplay);

  const account = queryResult.data?.account;

  function onChange(value: boolean) {
    update({
      variables: {
        isAutoReplayEnabled: value,
        accountId,
      },
    });
  }

  // Only owners and super admins can toggle auto-replay
  const canChangeAutoReplay =
    isOwnerRole(account?.currentUserRole) ||
    isSuperAdminRole(account?.currentUserRole);

  return {
    canAutoReplay:
      account?.isUnlimited || account?.plan?.hasAutoReplay || false,
    canChangeAutoReplay,
    error: getErrorMessage(queryResult.error || updateResults.error),
    isAutoReplayEnabled: account?.isAutoReplayEnabled || false,
    isBusy: updateResults.loading,
    isLoading: queryResult.loading,
    onChange,
  };
}

export default useAutoReplay;
